import React from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
// Removed boolean Frozen checkbox in favor of numeric input
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import CustomerSelect from '../../components/CustomerSelect';
import SupplierSelect from '../../components/SupplierSelect';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { adalApiFetch } from '../../../adalConfig';
import { textfieldDateFormat } from '../../utils/dateFunctions';
import downloadPOD from '../../utils/downloadPOD';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// exceljs will be dynamically imported only when exporting to reduce bundle size
// Access constants for permission-based gating
import { ACCESS, ACCESS_SECTIONS } from '../../utils/constants/access';
import { checkAccess } from '../../utils/roleFunctions';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { handlePagination } from '../../utils/apiFunction';

// Placeholder Runs listing page. Will display generated runs for a selected date range.
// Future enhancements:
// - Fetch runs via /logistics/runs?startDate=&endDate=
// - Display grouped by Run with ordered PartnerRunData items
// - Export buttons (Excel / PDF)
// - Link to manage page for configuration

const MySwal = withReactContent(Swal);

const RunsPage = (props) => {
  // Helper: show blank instead of 0 for numeric cells/exports
  const zeroBlank = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v ?? '';
    return n === 0 ? '' : n;
  };

  const [runDate, setRunDate] = React.useState(new Date());
  const [loading, setLoading] = React.useState(false);
  const [rebuilding, setRebuilding] = React.useState(false);
  const [groups, setGroups] = React.useState([]); // [{ runId, runDescription, items: [...] }]
  const [runPodGenerating, setRunPodGenerating] = React.useState(null); // runId currently generating POD PDF
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [dragging, setDragging] = React.useState({ runId: null, id: null });
  // Unassigned orders state
  const [missingSOs, setMissingSOs] = React.useState([]); // [{id,myobid,status,customerName,date}]
  const [missingPOs, setMissingPOs] = React.useState([]); // [{id,myobid,status,supplierName,date}]
  const [missingOnHoldSOs, setMissingOnHoldSOs] = React.useState([]); // on-hold SOs only
  const [showMissingSODialog, setShowMissingSODialog] = React.useState(false);
  const [showMissingPODialog, setShowMissingPODialog] = React.useState(false);
  const [showOnHoldSODialog, setShowOnHoldSODialog] = React.useState(false);
  const [loadingUnassigned, setLoadingUnassigned] = React.useState(false);

  // Add/Edit modal state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('add'); // 'add' | 'edit'
  const [dialogRunId, setDialogRunId] = React.useState(null);
  const [editingItemId, setEditingItemId] = React.useState(null);
  const [orderKind, setOrderKind] = React.useState('MANUAL'); // 'SO' | 'PO' | 'MANUAL'
  const [partnerType, setPartnerType] = React.useState('C'); // 'C' | 'S'
  const [companyOption, setCompanyOption] = React.useState(null); // object from CustomerSelect/SupplierSelect
  const [orderIdText, setOrderIdText] = React.useState('');
  const [windowStart, setWindowStart] = React.useState('');
  const [windowEnd, setWindowEnd] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [extraInfo, setExtraInfo] = React.useState('');
  const [binCount, setBinCount] = React.useState('');
  const [palletCount, setPalletCount] = React.useState('');
  // Species-specific manual counts
  const [srlBagCount, setSrlBagCount] = React.useState(''); // Sydney Rock - Bag
  const [srlBoxCount, setSrlBoxCount] = React.useState(''); // Sydney Rock - Box
  const [pacBagCount, setPacBagCount] = React.useState(''); // Pacific - Bag
  const [pacBoxCount, setPacBoxCount] = React.useState(''); // Pacific - Box
  const [halfShellCount, setHalfShellCount] = React.useState('');
  const [frozenCount, setFrozenCount] = React.useState('');
  // Order lookup state (for SO/PO selection)
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [orderOptions, setOrderOptions] = React.useState([]); // { id, myobid, date, label }

  const resetDialog = () => {
    setDialogMode('add');
    setDialogRunId(null);
    setEditingItemId(null);
    setOrderKind('MANUAL');
    setPartnerType('C');
    setCompanyOption(null);
  setOrderIdText('');
    setWindowStart('');
    setWindowEnd('');
    setAddress('');
    setExtraInfo('');
  setBinCount('');
  setPalletCount('');
  setSrlBagCount('');
  setSrlBoxCount('');
  setPacBagCount('');
  setPacBoxCount('');
  setHalfShellCount('');
  setFrozenCount('');
  };

  const openAddForRun = (runId) => {
    resetDialog();
    setDialogMode('add');
    setDialogRunId(runId);
    setDialogOpen(true);
  };

  const openEditForItem = (group, item) => {
    resetDialog();
    setDialogMode('edit');
    setDialogRunId(group.runId);
    setEditingItemId(item.id);
    const ok = item.orderType === 'SO' ? 'SO' : (item.orderType === 'PO' ? 'PO' : 'MANUAL');
    setOrderKind(ok);
    setPartnerType(item.partnerType === 'S' ? 'S' : 'C');
    // Prefill company (include myobid so CustomerSelect label renders correctly)
    if (item.partnerId) {
      // Only set id & name; allow CustomerSelect to resolve proper customer MYOB id after it loads
      setCompanyOption({ id: item.partnerId, name: item.company || '' });
    }
    setAddress(item.address || '');
    setExtraInfo(item.comments || '');
    // Prefill time window fields (trim to HH:mm if seconds provided)
    if (item.windowStart) setWindowStart(String(item.windowStart).substring(0,5));
    if (item.windowEnd) setWindowEnd(String(item.windowEnd).substring(0,5));
  if (item.binCount !== undefined && item.binCount !== null) setBinCount(String(item.binCount));
  if (item.palletCount !== undefined && item.palletCount !== null) setPalletCount(String(item.palletCount));
  if (item.sydneyRockLiveBagCount !== undefined && item.sydneyRockLiveBagCount !== null) setSrlBagCount(String(item.sydneyRockLiveBagCount));
  else if (item.qtySydneyRockLiveBag !== undefined && item.qtySydneyRockLiveBag !== null) setSrlBagCount(String(item.qtySydneyRockLiveBag));
  if (item.sydneyRockLiveBoxCount !== undefined && item.sydneyRockLiveBoxCount !== null) setSrlBoxCount(String(item.sydneyRockLiveBoxCount));
  else if (item.qtySydneyRockLiveBox !== undefined && item.qtySydneyRockLiveBox !== null) setSrlBoxCount(String(item.qtySydneyRockLiveBox));
  if (item.pacificLiveBagCount !== undefined && item.pacificLiveBagCount !== null) setPacBagCount(String(item.pacificLiveBagCount));
  else if (item.qtyPacificLiveBag !== undefined && item.qtyPacificLiveBag !== null) setPacBagCount(String(item.qtyPacificLiveBag));
  if (item.pacificLiveBoxCount !== undefined && item.pacificLiveBoxCount !== null) setPacBoxCount(String(item.pacificLiveBoxCount));
  else if (item.qtyPacificLiveBox !== undefined && item.qtyPacificLiveBox !== null) setPacBoxCount(String(item.qtyPacificLiveBox));
  if (item.halfShellCount !== undefined && item.halfShellCount !== null) setHalfShellCount(String(item.halfShellCount));
  else if (item.qtyHalfshell !== undefined && item.qtyHalfshell !== null) setHalfShellCount(String(item.qtyHalfshell));
  if (item.frozenCount !== undefined && item.frozenCount !== null) setFrozenCount(String(item.frozenCount));
  else if (item.qtyFrozen !== undefined && item.qtyFrozen !== null) setFrozenCount(String(item.qtyFrozen));
    setDialogOpen(true);
  };

  // no dynamic fetch — CustomerSelect/SupplierSelect will load all on mount

  // Keep partnerType aligned with orderKind for SO/PO
  React.useEffect(() => {
    if (orderKind === 'SO') setPartnerType('C');
    else if (orderKind === 'PO') setPartnerType('S');
  }, [orderKind]);

  // Reset order selection context when switching type or company
  React.useEffect(() => {
    setOrderIdText('');
    setOrderOptions([]);
  }, [orderKind, companyOption?.id]);

  const fetchUpcomingOrders = React.useCallback(async () => {
    if (orderKind === 'MANUAL') return;
    if (!companyOption || !companyOption.id) return;
    try {
      setOrdersLoading(true);
      const qs = new URLSearchParams();
      qs.set('partnerType', orderKind === 'SO' ? 'C' : 'S');
      qs.set('partnerId', String(companyOption.id));
      qs.set('orderType', orderKind);
      const url = `${baseUrl}/Logistics/GetUpcomingPartnerOrders?${qs.toString()}`;
      const rsp = await adalApiFetch(fetch, url, { method: 'GET' });
      const data = await rsp.json();
      if (Array.isArray(data)) {
        const opts = data.map(o => {
          const d = o.date || o.Date;
            const dateStr = d ? dayjs(d).format('DD/MM/YYYY') : '';
            const myob = o.myobid || o.Myobid || '';
            return {
              id: o.id || o.Id,
              myobid: myob,
              date: d,
              label: myob ? `${myob} - ${dateStr}` : dateStr
            };
        });
        setOrderOptions(opts);
      } else {
        setOrderOptions([]);
      }
    } catch (e) {
      console.error('Order lookup failed', e);
      setOrderOptions([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [orderKind, companyOption, baseUrl]);

  // Auto fetch when dialog opens with SO/PO and company chosen
  React.useEffect(() => {
    if (dialogOpen && orderKind !== 'MANUAL' && companyOption && companyOption.id) {
      fetchUpcomingOrders();
    }
  }, [dialogOpen, orderKind, companyOption, fetchUpcomingOrders]);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSave = async () => {
    try {
      const effectivePartnerType = orderKind === 'SO' ? 'C' : (orderKind === 'PO' ? 'S' : partnerType);
      if (!companyOption || !companyOption.id) {
        await MySwal.fire({ icon: 'warning', title: 'Select a company' });
        return;
      }
      if (dialogMode === 'add' && orderKind !== 'MANUAL' && !orderIdText) {
        await MySwal.fire({ icon: 'warning', title: 'Enter an order Id' });
        return;
      }

  if (dialogMode === 'add') {
        const params = new URLSearchParams();
        params.set('RunId', String(dialogRunId));
        params.set('RunDate', textfieldDateFormat(runDate));
        params.set('PartnerType', effectivePartnerType);
        params.set('PartnerId', String(companyOption.id));
        if (orderKind !== 'MANUAL' && orderIdText) {
          params.set('OrderType', orderKind);
          params.set('OrderId', String(orderIdText));
        }
        if (windowStart) params.set('WindowStart', windowStart);
        if (windowEnd) params.set('WindowEnd', windowEnd);
        params.set('ExtraInfo', extraInfo || '');
        params.set('Address', address || '');
        if (binCount) params.set('BinCount', binCount);
        if (palletCount) params.set('PalletCount', palletCount);
        if (srlBagCount !== '') params.set('SydneyRockLiveBagCount', srlBagCount || '0');
        if (srlBoxCount !== '') params.set('SydneyRockLiveBoxCount', srlBoxCount || '0');
        if (pacBagCount !== '') params.set('PacificLiveBagCount', pacBagCount || '0');
        if (pacBoxCount !== '') params.set('PacificLiveBoxCount', pacBoxCount || '0');
        if (halfShellCount !== '') params.set('HalfShellCount', halfShellCount || '0');
        if (frozenCount !== '') params.set('FrozenCount', frozenCount || '0');
        const url = `${baseUrl}/Logistics/AddDailyRunItem?${params.toString()}`;
        const rsp = await adalApiFetch(fetch, url, { method: 'POST' });
        await rsp.json();
      } else {
        const params = new URLSearchParams();
        params.set('Id', String(editingItemId));
        params.set('PartnerType', effectivePartnerType);
        params.set('PartnerId', String(companyOption.id));
        if (orderKind !== 'MANUAL' && orderIdText) {
          params.set('OrderType', orderKind);
          params.set('OrderId', String(orderIdText));
        } else {
          // Explicitly indicate manual to clear any existing order linkage
          params.set('OrderType', 'MANUAL');
        }
        if (windowStart) params.set('WindowStart', windowStart);
        if (windowEnd) params.set('WindowEnd', windowEnd);
        params.set('ExtraInfo', extraInfo || '');
        params.set('Address', address || '');
        if (binCount) params.set('BinCount', binCount);
        if (palletCount) params.set('PalletCount', palletCount);
        if (srlBagCount !== '') params.set('SydneyRockLiveBagCount', srlBagCount || '0');
        if (srlBoxCount !== '') params.set('SydneyRockLiveBoxCount', srlBoxCount || '0');
        if (pacBagCount !== '') params.set('PacificLiveBagCount', pacBagCount || '0');
        if (pacBoxCount !== '') params.set('PacificLiveBoxCount', pacBoxCount || '0');
        if (halfShellCount !== '') params.set('HalfShellCount', halfShellCount || '0');
        if (frozenCount !== '') params.set('FrozenCount', frozenCount || '0');
        const url = `${baseUrl}/Logistics/UpdateDailyRunItem?${params.toString()}`;
        const rsp = await adalApiFetch(fetch, url, { method: 'POST' });
        await rsp.json();
      }

      setDialogOpen(false);
      await loadRunsForDate();
      await MySwal.fire({ icon: 'success', title: 'Saved' });
    } catch (e) {
      console.error(e);
      await MySwal.fire({ icon: 'error', title: 'Save failed' });
    }
  };

  const loadRunsForDate = async () => {
    setLoading(true);
    try {
      const qs = `RunDate=${encodeURIComponent(textfieldDateFormat(runDate))}`;
      const rsp = await adalApiFetch(fetch, `${baseUrl}/Logistics/GetRunDailySheetForDate?${qs}`);
      const raw = await rsp.json();
      const normalized = Array.isArray(raw) ? raw.map(g => ({
        runId: g.runId ?? g.RunId,
        runDescription: g.runDescription ?? g.RunDescription,
        items: Array.isArray(g.items ?? g.Items) ? (g.items ?? g.Items).map(i => ({
          id: i.id ?? i.Id,
          runOrder: i.runOrder ?? i.RunOrder,
          partnerType: i.partnerType ?? i.PartnerType, // 'C' or 'S'
          partnerId: i.partnerId ?? i.PartnerId,
          orderType: i.orderType ?? i.OrderType, // 'SO' or 'PO'
          orderId: i.orderId ?? i.OrderId,
      company: i.company ?? i.Company,
          address: i.address ?? i.Address,
          orderMyobId: i.orderMyobId ?? i.OrderMyobId,
            orderDescription: i.orderDescription ?? i.OrderDescription ?? '',
          qtySydneyRockLiveBag: i.qtySydneyRockLiveBag ?? i.QtySydneyRockLiveBag ?? 0,
          qtySydneyRockLiveBox: i.qtySydneyRockLiveBox ?? i.QtySydneyRockLiveBox ?? 0,
          qtyPacificLiveBag: i.qtyPacificLiveBag ?? i.QtyPacificLiveBag ?? 0,
          qtyPacificLiveBox: i.qtyPacificLiveBox ?? i.QtyPacificLiveBox ?? 0,
          qtyFrozen: i.qtyFrozen ?? i.QtyFrozen ?? 0,
          qtyHalfshell: i.qtyHalfshell ?? i.QtyHalfshell ?? 0,
          sydneyRockLiveBagCount: i.sydneyRockLiveBagCount ?? i.SydneyRockLiveBagCount ?? null,
          sydneyRockLiveBoxCount: i.sydneyRockLiveBoxCount ?? i.SydneyRockLiveBoxCount ?? null,
          pacificLiveBagCount: i.pacificLiveBagCount ?? i.PacificLiveBagCount ?? null,
          pacificLiveBoxCount: i.pacificLiveBoxCount ?? i.PacificLiveBoxCount ?? null,
          halfShellCount: i.halfShellCount ?? i.HalfShellCount ?? null,
          frozenCount: i.frozenCount ?? i.FrozenCount ?? null,
          binCount: i.binCount ?? i.BinCount ?? 0,
          palletCount: i.palletCount ?? i.PalletCount ?? 0,
          temperature: i.temperature ?? i.Temperature,
          deliveryTime: i.deliveryTime ?? i.DeliveryTime,
          windowStart: i.windowStart ?? i.WindowStart,
          windowEnd: i.windowEnd ?? i.WindowEnd,
          addedByUser: i.addedByUser ?? i.AddedByUser ?? false,
          comments: i.comments ?? i.Comments,
          isCompleted: i.isCompleted ?? i.IsCompleted,
        })) : [],
      })) : [];
      // order items by RunOrder
      normalized.forEach(g => g.items.sort((a, b) => (a.runOrder ?? 0) - (b.runOrder ?? 0)));
      setGroups(normalized);
    } catch (e) {
      console.error('Failed to load runs', e);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all SO / PO for selected date and compute which are not present in run sheet items
  const fetchUnassignedOrders = React.useCallback(async () => {
    const dateStr = textfieldDateFormat(runDate); // assumed YYYY-MM-DD
    const soOnSheet = new Set();
    const poOnSheet = new Set();
    groups.forEach(g => (g.items || []).forEach(it => {
      if (it.orderType === 'SO' && it.orderId) soOnSheet.add((it.orderId));
      if (it.orderType === 'PO' && it.orderId) poOnSheet.add((it.orderId));
    }));
    try {
      setLoadingUnassigned(true);
      // Use shared pagination helper (expects url WITHOUT params; supplies startDate/endDate & start internally)
      const soUrl = `${baseUrl}/SalesOrder/GetByShipmentDate`;
      const poUrl = `${baseUrl}/PurchaseOrder/Get`;
      // Sales Orders: same-day window (inclusive in controller)
      const allSOs = await handlePagination(fetch, soUrl, dateStr, dateStr, {});
      // Purchase Orders: endDate appears exclusive (< endDate) so add +1 day
      const endDatePO = dayjs(runDate).add(1, 'day').format('YYYY-MM-DD');
      const allPOs = await handlePagination(fetch, poUrl, dateStr, endDatePO, {});
      // Split Sales Orders into Open vs On Hold (controller no longer provides on-hold separately)
      const filteredSOsOpen = (allSOs || []).filter(o => o.status === 'Open');
      const filteredSOsOnHold = (allSOs || []).filter(o => o.status === 'On Hold');
      const filteredPOs = (allPOs || []).filter(o => (o.status === 'Open' || o.status === 'On Hold'));
      const missingSOList = filteredSOsOpen.filter(o => {
        const my = o.id
        return !soOnSheet.has(my);
      }).map(o => ({
        id: o.Id || o.id,
        myobid: (o.Myobid || o.myobid || '').trim(),
        status: o.status,
        customerName: o.customer ? (o.customer.Name || o.customer.name) : '',
        date: o.OrderDate || o.orderDate
      }));
      const missingOnHoldSOList = filteredSOsOnHold.filter(o => {
        const my = o.id;
        return !soOnSheet.has(my);
      }).map(o => ({
        id: o.Id || o.id,
        myobid: (o.Myobid || o.myobid || '').trim(),
        status: o.status,
        customerName: o.customer ? (o.customer.Name || o.customer.name) : '',
        date: o.OrderDate || o.orderDate
      }));
      const missingPOList = filteredPOs.filter(o => {
        const my = o.id;
        if (!my) return false;
        return !poOnSheet.has(my);
      }).map(o => ({
        id: o.Id || o.id,
        myobid: (o.Myobid || o.myobid || '').trim(),
        status: o.status,
        supplierName: o.supplier ? (o.supplier.Name || o.supplier.name) : (o.SupplierName || ''),
        date: o.OrderDate || o.orderDate
      }));
      setMissingSOs(missingSOList);
      setMissingOnHoldSOs(missingOnHoldSOList);
      setMissingPOs(missingPOList);
    } catch (e) {
      console.error('Failed fetching unassigned orders', e);
      setMissingSOs([]);
      setMissingOnHoldSOs([]);
      setMissingPOs([]);
    } finally {
      setLoadingUnassigned(false);
    }
  }, [runDate, groups, baseUrl]);

  // Refresh unassigned whenever runDate or groups change
  React.useEffect(() => { fetchUnassignedOrders(); }, [fetchUnassignedOrders]);

  // Load runs whenever the selected date changes
  React.useEffect(() => { loadRunsForDate(); }, [runDate]);

  // Ensure SweetAlert dialogs appear above MUI Dialog
  React.useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-runs-swal-zfix','true');
    style.innerHTML = '.swal2-container{z-index:2000 !important;}';
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const myobOrderLink = (orderType, myobId) => {
    if (!myobId) return null;
      const tenant = process.env.REACT_APP_MYOB_TENANT ? process.env.REACT_APP_MYOB_TENANT : 'https://aoc.myobadvanced.com/Main?';
    if (orderType === 'SO') {
      return `${tenant}ScreenId=SO301000&OrderType=SO&OrderNbr=${encodeURIComponent(myobId)}`;
    }
    if (orderType === 'PO') {
        return `${tenant}ScreenId=PO301000&OrderType=PO&OrderNbr=${encodeURIComponent(myobId)}`;
    }
    return null;
  };

  // Reorder within a run group and persist to API
  const reorderInGroup = async (runId, sourceId, targetId) => {
    try {
      if (!runId || !sourceId || !targetId || sourceId === targetId) return;
      const gi = groups.findIndex(g => g.runId === runId);
      if (gi < 0) return;
      const runGroup = groups[gi];
      const sorted = [...(runGroup.items || [])].sort((a, b) => (a.runOrder ?? 0) - (b.runOrder ?? 0));
      const srcIdx = sorted.findIndex(i => i.id === sourceId);
      const tgtIdx = sorted.findIndex(i => i.id === targetId);
      if (srcIdx < 0 || tgtIdx < 0) return;
      const [moved] = sorted.splice(srcIdx, 1);
      sorted.splice(tgtIdx, 0, moved);
      const resequenced = sorted.map((it, idx) => ({ ...it, runOrder: idx + 1 }));
      // optimistic UI update
      const nextGroups = groups.map((g, idx) => idx === gi ? { ...g, items: resequenced } : g);
      setGroups(nextGroups);

      // persist ordering
      const orderedIds = resequenced.map(i => i.id).join(',');
      const qs = new URLSearchParams();
      qs.set('RunId', String(runId));
      qs.set('RunDate', textfieldDateFormat(runDate));
      qs.set('OrderedIds', orderedIds);
      const url = `${baseUrl}/Logistics/UpdateDailyRunOrder?${qs.toString()}`;
      const rsp = await adalApiFetch(fetch, url, { method: 'POST' });
      // best-effort validation; ignore body or minor casing
      await rsp.json().catch(() => ({}));
      // tiny toast success
      MySwal.fire({ toast: true, position: 'bottom', icon: 'success', title: 'Order saved', showConfirmButton: false, timer: 1200 });
    } catch (e) {
      console.error('Reorder failed', e);
      MySwal.fire({ icon: 'error', title: 'Reorder failed' });
      // reload to recover
      await loadRunsForDate();
    }
  };

  const rebuildForDate = async () => {
    const result = await MySwal.fire({
      title: 'Rebuild from template?',
      text: `This will remove ONLY Sales Order and Purchase Order items for ${textfieldDateFormat(runDate)} and then re-add new ones from the template. Manual entries will be kept.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, rebuild',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    setRebuilding(true);
    try {
      const qs = `RunDate=${encodeURIComponent(textfieldDateFormat(runDate))}`;
      const rsp = await adalApiFetch(fetch, `${baseUrl}/Logistics/RebuildRunDailyItemsForDate?${qs}`, { method: 'POST' });
      const data = await rsp.json();
      await MySwal.fire({
        icon: 'success',
        title: 'Rebuilt',
        html: `Date: <b>${textfieldDateFormat(runDate)}</b><br/>Removed: ${data.itemsRemoved ?? data.ItemsRemoved ?? data.itemsDeleted ?? data.ItemsDeleted ?? 0}<br/>Runs: ${data.runsProcessed ?? data.RunsProcessed ?? 0}<br/>Inserted: ${data.itemsInserted ?? data.ItemsInserted ?? 0}<br/>Skipped: ${data.itemsSkippedExisting ?? data.ItemsSkippedExisting ?? 0}`,
      });
      await loadRunsForDate();
    } catch (e) {
      console.error(e);
      await MySwal.fire({ icon: 'error', title: 'Rebuild failed', text: 'Check console for details.' });
    } finally {
      setRebuilding(false);
    }
  };

  // Export current view as CSV (flat rows)
  const exportCsv = () => {
    if (!groups || groups.length === 0) return;
    const rows = [];
    // Add new blank columns: Delivery Time, Temp, Received By (kept at end for manual fill)
  rows.push(['Run', '#', 'Delivery Window', 'Company', 'Description', 'Address', 'Order No', 'SR Bag', 'SR Box', 'PAC Bag', 'PAC Box', 'Frozen', 'Halfshell', 'BIN', 'PLT', 'Comments', 'Delivery Time', 'Temp', 'Received By']);
    groups.forEach(g => {
      (g.items || []).forEach(it => {
        const srBag = (it.sydneyRockLiveBagCount ?? it.qtySydneyRockLiveBag) ?? 0;
        const srBox = (it.sydneyRockLiveBoxCount ?? it.qtySydneyRockLiveBox) ?? 0;
        const pcBag = (it.pacificLiveBagCount ?? it.qtyPacificLiveBag) ?? 0;
        const pcBox = (it.pacificLiveBoxCount ?? it.qtyPacificLiveBox) ?? 0;
        const frz = (it.frozenCount ?? it.qtyFrozen ?? 0);
        const half = (it.halfShellCount ?? it.qtyHalfshell) ?? 0;
        const bin = it.binCount ?? 0;
        const plt = it.palletCount ?? 0;
        rows.push([
          g.runDescription ?? '',
          it.runOrder ?? '',
          it.deliveryTime ?? '',
          it.company ?? '',
          it.orderDescription || '',
          it.address ?? '',
          it.orderMyobId ?? '',
          zeroBlank(srBag),
          zeroBlank(srBox),
          zeroBlank(pcBag),
          zeroBlank(pcBox),
          zeroBlank(frz),
          zeroBlank(half),
          zeroBlank(bin),
          zeroBlank(plt),
          it.comments ?? '',
          '', // Delivery Time (actual) blank for manual capture separate from window
          '', // Temp blank
          ''  // Received By blank
        ]);
      });
    });

    const esc = (v) => {
      const s = String(v ?? '');
      if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RunSheet_${textfieldDateFormat(runDate)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Shared Excel builder used by multi and single exports
  const buildExcelForGroups = async (groupArray, fileNameBase) => {
    if (!groupArray || groupArray.length === 0) return;
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const dateStr = dayjs(runDate).format('DD/MM/YYYY');
  const header = ['#', 'Delivery Window', 'Company', 'Description', 'Address', 'Order No', 'SR Bag', 'SR Box', 'PAC Bag', 'PAC Box', 'Frozen', 'Halfshell', 'BIN', 'PLT', 'Comments', 'Delivery Time', 'Temp', 'Received By'];
    const usedSheetNames = new Set();
    const makeSheetName = (name, idx) => {
      let base = (name || `Run${idx+1}`).toString().replace(/[\/\\?*\[\]:]/g, '').substring(0, 28);
      if (!base.trim()) base = `Run${idx+1}`;
      let attempt = base; let c=1;
      while(usedSheetNames.has(attempt)) { attempt = `${base.substring(0, 28-String(c).length)}${c}`; c++; }
      usedSheetNames.add(attempt); return attempt;
    };
    groupArray.forEach((g, gi) => {
      const ws = wb.addWorksheet(makeSheetName(g.runDescription, gi), { views:[{ state:'frozen', ySplit:2 }] });
      const title = `${g.runDescription || 'Run'} - ${dateStr}`;
      ws.addRow([title]);
      ws.mergeCells(1,1,1,header.length);
      const titleRow = ws.getRow(1);
      titleRow.font = { size:16, bold:true };
      titleRow.alignment = { horizontal:'center' };
      ws.addRow(header);
      const headerRow = ws.getRow(2);
      headerRow.font = { bold:true };
      headerRow.alignment = { vertical:'middle', horizontal:'center', wrapText:true };
      headerRow.eachCell(cell => { cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF2196F3' } }; cell.font = { bold:true, color:{ argb:'FFFFFFFF' } }; });
      (g.items || []).forEach(it => {
        const srBag = (it.sydneyRockLiveBagCount ?? it.qtySydneyRockLiveBag) ?? 0;
        const srBox = (it.sydneyRockLiveBoxCount ?? it.qtySydneyRockLiveBox) ?? 0;
        const pcBag = (it.pacificLiveBagCount ?? it.qtyPacificLiveBag) ?? 0;
        const pcBox = (it.pacificLiveBoxCount ?? it.qtyPacificLiveBox) ?? 0;
        const frz = (it.frozenCount ?? it.qtyFrozen ?? 0);
        const half = (it.halfShellCount ?? it.qtyHalfshell) ?? 0;
        const bin = it.binCount ?? 0;
        const plt = it.palletCount ?? 0;
        const r = ws.addRow([
          it.runOrder ?? '',
          it.deliveryTime ?? '',
          it.company ?? '',
          it.orderDescription || '',
          it.address ?? '',
          it.orderMyobId ?? '',
          zeroBlank(srBag),
          zeroBlank(srBox),
          zeroBlank(pcBag),
          zeroBlank(pcBox),
          zeroBlank(frz),
          zeroBlank(half),
          zeroBlank(bin),
          zeroBlank(plt),
          it.comments ?? '',
          '', '', ''
        ]);
        r.font = { size:10 };
        r.alignment = { vertical:'middle', horizontal:'left', wrapText:true };
      });
  const widths = [4,14,26,28,40,12,9,9,9,9,8,10,6,6,30,14,10,20];
      widths.forEach((w,i)=> { ws.getColumn(i+1).width = w; });
      const dataStart = 3;
      const lastRow = ws.lastRow.number;
      const deliveryCol = 2;
      for (let r = dataStart; r <= lastRow; r++) {
        const row = ws.getRow(r);
        const nextRow = r < lastRow ? ws.getRow(r+1) : null;
        row.eachCell(cell => { cell.border = { top:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'}, bottom:{style:'thin'} }; });
        if (nextRow) {
          const currVal = row.getCell(deliveryCol).value || '';
          const nextVal = nextRow.getCell(deliveryCol).value || '';
          if (currVal !== nextVal) {
            row.eachCell(cell => { cell.border = { ...cell.border, bottom:{ style:'double' } }; });
          }
        }
      }
      headerRow.eachCell(c => { c.border = { top:{style:'medium'}, left:{style:'thin'}, right:{style:'thin'}, bottom:{style:'medium'} }; });
    });
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${fileNameBase}_${textfieldDateFormat(runDate)}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(()=>URL.revokeObjectURL(a.href), 2000);
  };

  const exportExcel = async () => {
    if (!groups || groups.length === 0) return;
    try { await buildExcelForGroups(groups, 'RunSheet'); }
    catch(e){ console.error('Excel export failed', e); MySwal.fire({ icon:'error', title:'Excel export failed' }); }
  };

  // Shared PDF builder
  const buildPdfForGroups = (groupArray, fileNameBase) => {
    if (!groupArray || groupArray.length === 0) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleDate = dayjs(runDate).format('DD/MM/YYYY');
  const headerRow = ['#', 'Delivery Window', 'Company', 'Description', 'Address', 'Order No', 'SR Bag', 'SR Box', 'PAC Bag', 'PAC Box', 'Frozen', 'Halfshell', 'BIN', 'PLT', 'Comments', 'Delivery Time', 'Temp', 'Received By'];
    groupArray.forEach((g, gi) => {
      if (gi > 0) doc.addPage();
      const title = `${g.runDescription || 'Run'} - ${titleDate}`;
      doc.setFontSize(16);
      doc.text(title, pageWidth / 2, 14, { align: 'center' });
    const body = (g.items || []).map(it => {
    const srBag = (it.sydneyRockLiveBagCount ?? it.qtySydneyRockLiveBag) ?? 0;
    const srBox = (it.sydneyRockLiveBoxCount ?? it.qtySydneyRockLiveBox) ?? 0;
    const pcBag = (it.pacificLiveBagCount ?? it.qtyPacificLiveBag) ?? 0;
    const pcBox = (it.pacificLiveBoxCount ?? it.qtyPacificLiveBox) ?? 0;
    const frz = (it.frozenCount ?? it.qtyFrozen ?? 0);
    const half = (it.halfShellCount ?? it.qtyHalfshell) ?? 0;
    const bin = it.binCount ?? 0;
    const plt = it.palletCount ?? 0;
    return ([
          it.runOrder ?? '',
          it.deliveryTime ?? '',
          it.company ?? '',
          (it.orderDescription || ''),
      (it.address || '').substring(0, 80),
          it.orderMyobId ?? '',
    zeroBlank(srBag),
    zeroBlank(srBox),
    zeroBlank(pcBag),
    zeroBlank(pcBox),
    zeroBlank(frz),
    zeroBlank(half),
    zeroBlank(bin),
    zeroBlank(plt),
          (it.comments || '').substring(0, 60),
          '', '', ''
        ]);
        });
      autoTable(doc, {
        startY: 20,
        head: [headerRow],
        body,
        styles: { fontSize: 8, cellPadding: 2, lineWidth: 0.1 },
        headStyles: { fillColor: [33, 150, 243], lineWidth: 0.3 },
        didParseCell: (hookData) => {
          if (hookData.section === 'body') {
            const idx = hookData.row.index;
            if (idx < hookData.table.body.length - 1) {
              const curr = hookData.row.cells[1].raw || '';
              const next = hookData.table.body[idx + 1].cells[1].raw || '';
              if (curr !== next) {
                hookData.row.customGroupEnd = true;
              }
            }
          }
        },
        didDrawCell: (hookData) => {
          if (hookData.section === 'body' && hookData.row.customGroupEnd && hookData.column.index === hookData.table.columns.length - 1) {
            const d = hookData.doc;
            const y = hookData.cell.y + hookData.cell.height;
            const firstCell = hookData.row.cells[0];
            const lastCell = hookData.row.cells[hookData.row.cells.length - 1];
            if (firstCell && lastCell) {
              const x1 = firstCell.x; const x2 = lastCell.x + lastCell.width;
              if (Number.isFinite(x1) && Number.isFinite(x2) && Number.isFinite(y)) {
                d.setLineWidth(0.8);
                d.line(x1, y, x2, y);
                d.setLineWidth(0.1);
              }
            }
          }
        },
        didDrawPage: () => {
          doc.setFontSize(8);
          doc.text(`Page ${gi + 1} of ${groupArray.length}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 8);
        }
      });
    });
    doc.save(`${fileNameBase}_${textfieldDateFormat(runDate)}.pdf`);
  };

  const exportPdf = () => {
    if (!groups || groups.length === 0) return;
    try { buildPdfForGroups(groups, 'RunSheet'); }
    catch(e){ console.error('PDF export failed', e); MySwal.fire({ icon:'error', title:'PDF export failed' }); }
  };

  const exportSingleRunExcel = async (group) => {
    if (!group) return; 
    try { await buildExcelForGroups([group], `RunSheet_${(group.runDescription || 'Run').replace(/\s+/g,'_')}`); }
    catch(e){ console.error('Single run Excel export failed', e); MySwal.fire({ icon:'error', title:'Excel export failed' }); }
  };

  const exportSingleRunPdf = (group) => {
    if (!group) return; 
    try { buildPdfForGroups([group], `RunSheet_${(group.runDescription || 'Run').replace(/\s+/g,'_')}`); }
    catch(e){ console.error('Single run PDF export failed', e); MySwal.fire({ icon:'error', title:'PDF export failed' }); }
  };

  // Generate combined POD PDF (packing slip style) for a specific run's Sales Orders only
  const downloadPODForRun = async (g) => {
    try {
      if (!g || !Array.isArray(g.items)) return;
      const soIds = g.items.filter(it => it.orderType === 'SO' && it.orderId).map(it => it.orderId);
      if (soIds.length === 0) {
        await MySwal.fire({ icon: 'info', title: 'No Sales Orders', text: 'This run has no Sales Order lines to include.' });
        return;
      }
      setRunPodGenerating(g.runId);
      await downloadPOD(soIds, runDate, null, { runDescription: g.runDescription });
    } catch (e) {
      console.error(e);
      await MySwal.fire({ icon: 'error', title: 'POD generation failed' });
    } finally {
      setRunPodGenerating(null);
    }
  };

  const isPast = dayjs(runDate).startOf('day').isBefore(dayjs().startOf('day'));

  // Simplified permission check using shared checkAccess helper.
  // Expectation: an array of access objects (e.g. [{ key:'Logistics', value:'edit' }, ...])
  // is available on one of: props.access, props.user?.access, window.userAccess, window.access, window.AppAccess.
  const canEditLogistics = React.useMemo(() => {
    const candidate = props?.access || props?.user?.access || (typeof window !== 'undefined' && (window.userAccess || window.access || window.AppAccess)) || [];
    return checkAccess(candidate, { key: ACCESS_SECTIONS.LOGISTICS, value: ACCESS.EDIT });
  }, [props]);

  // New: master level access required for rebuild action
  const canMasterLogistics = React.useMemo(() => {
    const candidate = props?.access || props?.user?.access || (typeof window !== 'undefined' && (window.userAccess || window.access || window.AppAccess)) || [];
    return checkAccess(candidate, { key: ACCESS_SECTIONS.LOGISTICS, value: ACCESS.MASTER });
  }, [props]);

  const canModify = canEditLogistics && !isPast; // unified flag for enabling edit/add/delete/reorder

  const guardedOpenAddForRun = (runId) => {
    if (!canModify) return; // no-op for past dates or insufficient access
    openAddForRun(runId);
  };
  const guardedOpenEdit = (g, it) => {
    if (!canModify) return;
    openEditForItem(g, it);
  };

  // Override reorder to respect isPast
  const guardedReorder = async (runId, sourceId, targetId) => {
    if (!canModify) return; // skip silently
    await reorderInGroup(runId, sourceId, targetId);
  };

  const guardedHandleDialogSave = async () => {
    if (!canModify) return; // Cannot save edits on past dates or without permission
    await handleDialogSave();
  };

  const deleteDailyItem = async (itemId) => {
    if (!canModify) return; // guard
    // Find item to determine warning nuance
    const item = groups.flatMap(g => g.items || []).find(i => i.id === itemId);
    const isAutoSOPO = item && (item.orderType === 'SO' || item.orderType === 'PO') && !item.addedByUser;
    const warningText = isAutoSOPO
      ? "This will remove the line from today's run sheet now, but because it originates from the template it will reappear shortly."
      : "This will permanently remove the line from today's run sheet.";
    const result = await MySwal.fire({
      title: 'Delete this line?',
      text: warningText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    try {
      const qs = new URLSearchParams();
      qs.set('Id', String(itemId));
      const url = `${baseUrl}/Logistics/DeleteDailyRunItem?${qs.toString()}`;
      const rsp = await adalApiFetch(fetch, url, { method: 'POST' });
      await rsp.json().catch(()=>({}));
      // Optimistically remove from state
      setGroups(prev => prev.map(g => ({...g, items: g.items.filter(i => i.id !== itemId)})));
      MySwal.fire({ toast:true, position:'bottom', icon:'success', title:'Deleted', timer:1200, showConfirmButton:false });
    } catch (e) {
      console.error('Delete failed', e);
      MySwal.fire({ icon:'error', title:'Delete failed' });
    }
  };

  return (
    <React.Fragment>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6} lg={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={2} alignItems="center">
              <DatePicker
                label="Run date"
                value={dayjs(runDate)}
                onChange={(v) => setRunDate(v ? v.toDate() : new Date())}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <Button variant="contained" onClick={loadRunsForDate} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </Stack>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6} lg={6} display="flex" alignItems="center" gap={1}>
          {canMasterLogistics && !isPast && (
            <Button variant="outlined" color="warning" onClick={rebuildForDate} disabled={rebuilding || loading}>
              {rebuilding ? 'Rebuilding…' : 'Rebuild from template'}
            </Button>
          )}
          <Button variant="outlined" onClick={exportCsv} disabled={loading || groups.length === 0}>Export CSV</Button>
          <Button variant="outlined" onClick={exportExcel} disabled={loading || groups.length === 0}>Export Excel</Button>
          <Button variant="outlined" onClick={exportPdf} disabled={loading || groups.length === 0}>Export PDF</Button>
          {canEditLogistics && (
            <Button variant="text" href="/logistics/runs/manage">Manage Runs</Button>
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Runs for {textfieldDateFormat(runDate)}
      </Typography>

      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <Tooltip title="Sales Orders for this date not on any run sheet">
          <span>
            <Badge color={missingSOs.length ? 'error' : 'default'} badgeContent={missingSOs.length} overlap="circular">
              <Chip
                icon={<ShoppingCartIcon />}
                label={loadingUnassigned ? 'SO…' : (missingSOs.length === 0 ? 'No Unassigned SO' : 'Unassigned SO')}
                size="small"
                variant={missingSOs.length ? 'filled' : 'outlined'}
                onClick={() => { if (!loadingUnassigned && missingSOs.length > 0) setShowMissingSODialog(true); }}
                clickable={!loadingUnassigned && missingSOs.length > 0}
              />
            </Badge>
          </span>
        </Tooltip>
        <Tooltip title="On-Hold Sales Orders for this date (not on any run sheet)">
          <span>
            <Badge color={missingOnHoldSOs.length ? 'warning' : 'default'} badgeContent={missingOnHoldSOs.length} overlap="circular">
              <Chip
                icon={<ShoppingCartIcon />}
                label={loadingUnassigned ? 'On-Hold…' : (missingOnHoldSOs.length === 0 ? 'No On-Hold SO' : 'On-Hold SO')}
                size="small"
                variant={missingOnHoldSOs.length ? 'filled' : 'outlined'}
                onClick={() => { if (!loadingUnassigned && missingOnHoldSOs.length > 0) setShowOnHoldSODialog(true); }}
                clickable={!loadingUnassigned && missingOnHoldSOs.length > 0}
              />
            </Badge>
          </span>
        </Tooltip>
        <Tooltip title="Purchase Orders for this date not on any run sheet">
          <span>
            <Badge color={missingPOs.length ? 'warning' : 'default'} badgeContent={missingPOs.length} overlap="circular">
              <Chip
                icon={<LocalShippingIcon />}
                label={loadingUnassigned ? 'PO…' : (missingPOs.length === 0 ? 'No Unassigned PO' : 'Unassigned PO')}
                size="small"
                variant={missingPOs.length ? 'filled' : 'outlined'}
                onClick={() => { if (!loadingUnassigned && missingPOs.length > 0) setShowMissingPODialog(true); }}
                clickable={!loadingUnassigned && missingPOs.length > 0}
              />
            </Badge>
          </span>
        </Tooltip>
      </Stack>

      {groups.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary">No runs to display.</Typography>
      )}

      <Grid container spacing={2}>
        {groups.map((g) => (
          <Grid item xs={12} key={g.runId}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">{g.runDescription}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => downloadPODForRun(g)} disabled={runPodGenerating === g.runId || g.items.filter(it=>it.orderType==='SO' && it.orderId).length===0}>
                    {runPodGenerating === g.runId ? 'Generating…' : 'PODs'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => exportSingleRunExcel(g)} disabled={g.items.length===0}>Excel</Button>
                  <Button size="small" variant="outlined" onClick={() => exportSingleRunPdf(g)} disabled={g.items.length===0}>PDF</Button>
                  {canEditLogistics && (
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => guardedOpenAddForRun(g.runId)} disabled={!canModify}>Add</Button>
                  )}
                </Stack>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
            <TableCell>Delivery Window</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Order No</TableCell>
                      <TableCell align="right">SR Bag</TableCell>
                      <TableCell align="right">SR Box</TableCell>
                      <TableCell align="right">PAC Bag</TableCell>
                      <TableCell align="right">PAC Box</TableCell>
                      <TableCell align="right">Frozen</TableCell>
                      <TableCell align="right">Halfshell</TableCell>
                      <TableCell align="right">BIN</TableCell>
                      <TableCell align="right">PLT</TableCell>
                      <TableCell>Comments</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.items.map((it, idx) => {
                      const link = myobOrderLink(it.orderType, it.orderMyobId);
                      const next = g.items[idx + 1];
                      const currTime = (it.deliveryTime || '').toString();
                      const nextTime = next ? (next.deliveryTime || '').toString() : null;
                      const isGroupEnd = !next || currTime !== nextTime;
                      const rowSx = {
                        backgroundColor: it.partnerType === 'C' ? '#e3f2fd' : '#e8f5e9',
                        '& td:first-of-type': { borderLeft: '4px solid', borderLeftColor: it.partnerType === 'C' ? '#64b5f6' : '#81c784' },
                        cursor: 'grab',
                        opacity: dragging.id === it.id ? 0.6 : 1,
                        ...(isGroupEnd ? { '& td': { borderBottom: 'double 4px rgba(0,0,0,0.65)' } } : {})
                      };
                      return (
                        <TableRow
                          key={it.id}
                          sx={rowSx}
                          draggable={canModify}
                          onDragStart={() => { if (canModify) setDragging({ runId: g.runId, id: it.id }); }}
                          onDragOver={(e) => { if (canModify) e.preventDefault(); }}
                          onDrop={() => { if (canModify && dragging.runId === g.runId) { guardedReorder(g.runId, dragging.id, it.id); } setDragging({ runId: null, id: null }); }}
                          onDragEnd={() => setDragging({ runId: null, id: null })}
                        >
                          <TableCell>{it.runOrder}</TableCell>
                          <TableCell>{it.deliveryTime || ''}</TableCell>
                          <TableCell width={240}>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                              {it.company || ''}
                              {it.addedByUser && (
                                <Tooltip title="This order was manually added by user">
                                  <span style={{ color:'#ff9800', fontWeight:'bold' }}>*</span>
                                </Tooltip>
                              )}
                            </span>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.orderDescription || ''}</TableCell>
                          <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.address || ''}</TableCell>
                          <TableCell>
                            {link ? <Link href={link} target="_blank" rel="noopener">{it.orderMyobId}</Link> : (it.orderMyobId || '')}
                          </TableCell>
                          <TableCell align="right">{zeroBlank((it.sydneyRockLiveBagCount ?? it.qtySydneyRockLiveBag) ?? 0)}</TableCell>
                          <TableCell align="right">{zeroBlank((it.sydneyRockLiveBoxCount ?? it.qtySydneyRockLiveBox) ?? 0)}</TableCell>
                          <TableCell align="right">{zeroBlank((it.pacificLiveBagCount ?? it.qtyPacificLiveBag) ?? 0)}</TableCell>
                          <TableCell align="right">{zeroBlank((it.pacificLiveBoxCount ?? it.qtyPacificLiveBox) ?? 0)}</TableCell>
                          <TableCell align="right">{zeroBlank((it.frozenCount ?? it.qtyFrozen ?? 0))}</TableCell>
                          <TableCell align="right">{zeroBlank((it.halfShellCount ?? it.qtyHalfshell) ?? 0)}</TableCell>
                          <TableCell align="right">{zeroBlank(it.binCount ?? 0)}</TableCell>
                          <TableCell align="right">{zeroBlank(it.palletCount ?? 0)}</TableCell>
                          <TableCell sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.comments || ''}</TableCell>
                          <TableCell>
                            {canEditLogistics && (
                              <>
                                <Tooltip title={!canModify ? (isPast ? 'Past date - edit disabled' : 'No edit permission or date locked') : 'Edit'}>
                                  <span>
                                    <IconButton size="small" onClick={() => guardedOpenEdit(g, it)} disabled={!canModify}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {canModify && (
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => deleteDailyItem(it.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen && canModify} onClose={handleDialogClose} fullWidth maxWidth="md">
        <DialogTitle>{dialogMode === 'add' ? 'Add Run Item' : 'Edit Run Item'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="entry-type-label">Entry Type</InputLabel>
                <Select labelId="entry-type-label" label="Entry Type" value={orderKind} onChange={(e) => setOrderKind(e.target.value)}>
                  <MenuItem value="SO">Sales Order</MenuItem>
                  <MenuItem value="PO">Purchase Order</MenuItem>
                  <MenuItem value="MANUAL">Manual Entry</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={orderKind !== 'MANUAL'}>
                <InputLabel id="partner-type-label">Partner Type</InputLabel>
                <Select labelId="partner-type-label" label="Partner Type" value={partnerType} onChange={(e) => setPartnerType(e.target.value)}>
                  <MenuItem value="C">Customer</MenuItem>
                  <MenuItem value="S">Supplier</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              {partnerType === 'S' ? (
                <SupplierSelect state={companyOption} setState={setCompanyOption} />
              ) : (
                <CustomerSelect state={companyOption} setState={setCompanyOption} frmrId={companyOption?.id} />
              )}
            </Grid>

            {orderKind !== 'MANUAL' && dialogMode === 'add' && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  size="small"
                  fullWidth
                  loading={ordersLoading}
                  options={orderOptions}
                  getOptionLabel={(o) => o.label || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={orderOptions.find(o => String(o.id) === String(orderIdText)) || null}
                  onChange={(_, val) => setOrderIdText(val ? String(val.id) : '')}
                  onOpen={() => { if (orderOptions.length === 0) fetchUpcomingOrders(); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={orderKind === 'SO' ? 'Sales Order' : 'Purchase Order'}
                      placeholder={companyOption ? 'Select order…' : 'Select a company first'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {ordersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                      disabled={!companyOption || !companyOption.id}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileTimePicker
                  ampm
                  minutesStep={5}
                  label="Window Start"
                  value={windowStart ? dayjs(`2000-01-01 ${windowStart}`) : null}
                  onChange={(v) => setWindowStart(v ? dayjs(v).format('HH:mm') : '')}
                  format="h:mm a"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileTimePicker
                  ampm
                  minutesStep={5}
                  label="Window End"
                  value={windowEnd ? dayjs(`2000-01-01 ${windowEnd}`) : null}
                  onChange={(v) => setWindowEnd(v ? dayjs(v).format('HH:mm') : '')}
                  format="h:mm a"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address Override" size="small" fullWidth multiline minRows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="BIN"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={binCount}
                onChange={(e) => setBinCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="PLT"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={palletCount}
                onChange={(e) => setPalletCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="SR Bag"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={srlBagCount}
                onChange={(e) => setSrlBagCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="SR Box"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={srlBoxCount}
                onChange={(e) => setSrlBoxCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="PAC Bag"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={pacBagCount}
                onChange={(e) => setPacBagCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="PAC Box"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={pacBoxCount}
                onChange={(e) => setPacBoxCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Frozen"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={frozenCount}
                onChange={(e) => setFrozenCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Halfshell"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={halfShellCount}
                onChange={(e) => setHalfShellCount(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Comments" size="small" fullWidth multiline minRows={2} value={extraInfo} onChange={(e) => setExtraInfo(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={guardedHandleDialogSave} disabled={!canModify}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Unassigned Sales Orders */}
      <Dialog open={showMissingSODialog} onClose={() => setShowMissingSODialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Unassigned Sales Orders ({missingSOs.length})</DialogTitle>
        <DialogContent dividers>
          {missingSOs.length === 0 && <Typography variant="body2" color="text.secondary">All Sales Orders are on run sheets.</Typography>}
          {missingSOs.length > 0 && (
            <List dense>
              {missingSOs.map(o => {
                const link = o.myobid ? myobOrderLink('SO', o.myobid) : null;
                return (
                  <ListItem key={o.id} disablePadding>
                    <ListItemText
                      primary={<>{link ? <Link href={link} target="_blank" rel="noopener">{o.myobid}</Link> : o.myobid} – {o.customerName || 'Customer'} ({o.status})</>}
                      secondary={o.date ? dayjs(o.date).format('DD/MM/YYYY') : ''}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingSODialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: On-Hold Sales Orders */}
      <Dialog open={showOnHoldSODialog} onClose={() => setShowOnHoldSODialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>On-Hold Sales Orders ({missingOnHoldSOs.length})</DialogTitle>
        <DialogContent dividers>
          {missingOnHoldSOs.length === 0 && <Typography variant="body2" color="text.secondary">No on-hold Sales Orders for this date.</Typography>}
          {missingOnHoldSOs.length > 0 && (
            <List dense>
              {missingOnHoldSOs.map(o => {
                const link = o.myobid ? myobOrderLink('SO', o.myobid) : null;
                return (
                  <ListItem key={o.id} disablePadding>
                    <ListItemText
                      primary={<>{link ? <Link href={link} target="_blank" rel="noopener">{o.myobid}</Link> : o.myobid} – {o.customerName || 'Customer'} ({o.status})</>}
                      secondary={o.date ? dayjs(o.date).format('DD/MM/YYYY') : ''}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOnHoldSODialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Unassigned Purchase Orders */}
      <Dialog open={showMissingPODialog} onClose={() => setShowMissingPODialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Unassigned Purchase Orders ({missingPOs.length})</DialogTitle>
        <DialogContent dividers>
          {missingPOs.length === 0 && <Typography variant="body2" color="text.secondary">All Purchase Orders are on run sheets.</Typography>}
          {missingPOs.length > 0 && (
            <List dense>
              {missingPOs.map(o => {
                const link = o.myobid ? myobOrderLink('PO', o.myobid) : null;
                return (
                  <ListItem key={o.id} disablePadding>
                    <ListItemText
                      primary={<>{link ? <Link href={link} target="_blank" rel="noopener">{o.myobid}</Link> : o.myobid} – {o.supplierName || 'Supplier'} ({o.status})</>}
                      secondary={o.date ? dayjs(o.date).format('DD/MM/YYYY') : ''}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingPODialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default RunsPage;
